import { Theme } from '@mui/material';
import { createContext, useMemo, useState } from 'react';

import { darkTheme, lightTheme, themes } from '../constants/theme';
import { getDefaultEndTime, getDefaultStartTime } from '../constants/timetable';
import { CoursesList } from '../interfaces/Courses';
import { CourseDataMap, DisplayTimetablesMap, Term, TermDataList } from '../interfaces/Periods';
import { AppContextProviderProps } from '../interfaces/PropTypes';
import storage from '../utils/storage';

export interface IAppContext {
  currentTheme: string;
  setCurrentTheme: (newTheme: string) => void;

  themeObject: Theme;

  is12HourMode: boolean;
  setIs12HourMode: (newIs12HourMode: boolean) => void;

  isDarkMode: boolean;
  setIsDarkMode: (newIsDarkMode: boolean) => void;

  isSquareEdges: boolean;
  setIsSquareEdges: (newIsSquareEdges: boolean) => void;

  isShowOnlyOpenClasses: boolean;
  setisShowOnlyOpenClasses: (newisShowOnlyOpenClasses: boolean) => void;

  isDefaultUnscheduled: boolean;
  setIsDefaultUnscheduled: (newIsDefaultUnscheduled: boolean) => void;

  isHideClassInfo: boolean;
  setIsHideClassInfo: (newIsHideClassInfo: boolean) => void;

  isHideExamClasses: boolean;
  setIsHideExamClasses: (newIsHideExamClasses: boolean) => void;

  isConvertToLocalTimezone: boolean;
  setIsConvertToLocalTimezone: (newIsConvertToLocalTimezone: boolean) => void;

  alertMsg: string;
  setAlertMsg: (newErrorMsg: string) => void;

  alertFunction: () => void;
  setAlertFunction: (newAlertFunction: () => void) => void;

  errorVisibility: boolean;
  setErrorVisibility: (newErrorVisibility: boolean) => void;

  infoVisibility: boolean;
  setInfoVisibility: (newInfoVisibility: boolean) => void;

  autoVisibility: boolean;
  setAutoVisibility: (newAutoVisibility: boolean) => void;

  isDrag: boolean;
  setIsDrag: (newIsDrag: boolean) => void;

  days: string[];
  setDays(newDays: string[]): void;
  setDays(callback: (oldDays: string[]) => string[]): void;

  earliestStartTime: number;
  setEarliestStartTime(newEarliestStartTime: number): void;
  setEarliestStartTime(callback: (oldEarliestStartTime: number) => number): void;

  latestEndTime: number;
  setLatestEndTime(newLatestEndTime: number): void;
  setLatestEndTime(callback: (oldLatestEndTime: number) => number): void;

  term: Term;
  setTerm: (newTerm: Term) => void;

  termName: string;
  setTermName: (newTermName: string) => void;

  termsData: TermDataList;
  setTermsData: (newTermData: TermDataList) => void;

  termNumber: number;
  setTermNumber: (newTermNumber: number) => void;

  year: string;
  setYear: (newYear: string) => void;

  firstDayOfTerm: string;
  setFirstDayOfTerm: (newFirstDayOfTerm: string) => void;

  coursesList: CoursesList;
  setCoursesList: (newCoursesList: CoursesList) => void;

  selectedTimetable: number;
  setSelectedTimetable: (newSelectedTimetable: number) => void;

  displayTimetables: DisplayTimetablesMap;
  setDisplayTimetables: (newDisplayTimetable: any) => void;

  courseData: CourseDataMap;
  setCourseData: (newCourseData: CourseDataMap) => void;
}

export const AppContext = createContext<IAppContext>({
  themeObject: lightTheme(Object.keys(themes)[0]),

  currentTheme: Object.keys(themes)[0],
  setCurrentTheme: () => {},

  is12HourMode: false,
  setIs12HourMode: () => {},

  isDarkMode: false,
  setIsDarkMode: () => {},

  isSquareEdges: false,
  setIsSquareEdges: () => {},

  isShowOnlyOpenClasses: false,
  setisShowOnlyOpenClasses: () => {},

  isDefaultUnscheduled: true,
  setIsDefaultUnscheduled: () => {},

  isHideClassInfo: false,
  setIsHideClassInfo: () => {},

  isHideExamClasses: false,
  setIsHideExamClasses: () => {},

  isConvertToLocalTimezone: false,
  setIsConvertToLocalTimezone: () => {},

  alertMsg: '',
  setAlertMsg: () => {},

  alertFunction: () => {},
  setAlertFunction: () => {},

  errorVisibility: false,
  setErrorVisibility: () => {},

  infoVisibility: false,
  setInfoVisibility: () => {},

  autoVisibility: false,
  setAutoVisibility: () => {},

  isDrag: false,
  setIsDrag: () => {},

  days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
  setDays: () => {},

  earliestStartTime: getDefaultStartTime(true),
  setEarliestStartTime: () => {},

  latestEndTime: getDefaultEndTime(true),
  setLatestEndTime: () => {},

  term: '',
  setTerm: () => {},

  termName: ``,
  setTermName: () => {},

  termsData: [],
  setTermsData: () => {},

  termNumber: 0,
  setTermNumber: () => {},

  year: '',
  setYear: () => {},

  firstDayOfTerm: '',
  setFirstDayOfTerm: () => {},

  coursesList: [],
  setCoursesList: () => {},

  selectedTimetable: 0,
  setSelectedTimetable: () => {},

  displayTimetables: {},
  setDisplayTimetables: () => {},

  courseData: { map: [] },
  setCourseData: () => {},
});

const AppContextProvider = ({ children }: AppContextProviderProps) => {
  let termData = {
    year: '',
    term: '',
    termNumber: '',
    termName: '',
    firstDayOfTerm: '',
  };
  if (localStorage.getItem('termData')) {
    termData = JSON.parse(localStorage.getItem('termData')!);
  }
  const [currentTheme, setCurrentTheme] = useState<string>(storage.get('currentTheme'));
  const [is12HourMode, setIs12HourMode] = useState<boolean>(storage.get('is12HourMode'));
  const [isDarkMode, setIsDarkMode] = useState<boolean>(storage.get('isDarkMode'));
  const [isSquareEdges, setIsSquareEdges] = useState<boolean>(storage.get('isSquareEdges'));
  const [isShowOnlyOpenClasses, setisShowOnlyOpenClasses] = useState<boolean>(storage.get('isShowOnlyOpenClasses'));
  const [isDefaultUnscheduled, setIsDefaultUnscheduled] = useState<boolean>(storage.get('isDefaultUnscheduled'));
  const [isHideClassInfo, setIsHideClassInfo] = useState<boolean>(storage.get('isHideClassInfo'));
  const [isHideExamClasses, setIsHideExamClasses] = useState<boolean>(storage.get('isHideExamClasses'));
  const [isConvertToLocalTimezone, setIsConvertToLocalTimezone] = useState<boolean>(
    storage.get('isConvertToLocalTimezone'),
  );
  const [alertMsg, setAlertMsg] = useState<string>('');
  const [alertFunction, setAlertFunction] = useState<() => void>(() => () => {});
  const [errorVisibility, setErrorVisibility] = useState<boolean>(false);
  const [infoVisibility, setInfoVisibility] = useState<boolean>(false);
  const [autoVisibility, setAutoVisibility] = useState<boolean>(false);
  const [isDrag, setIsDrag] = useState<boolean>(false);
  const [days, setDays] = useState<string[]>(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']);
  const [earliestStartTime, setEarliestStartTime] = useState(getDefaultStartTime(isConvertToLocalTimezone));
  const [latestEndTime, setLatestEndTime] = useState(getDefaultEndTime(isConvertToLocalTimezone));
  const [termNumber, setTermNumber] = useState<number>(Number(termData.termNumber) || 0);
  const [term, setTerm] = useState<Term>(termData.term);
  const [termName, setTermName] = useState<string>('');
  const [year, setYear] = useState<string>(termData.year || '');
  const [termsData, setTermsData] = useState<TermDataList>([]);
  const [firstDayOfTerm, setFirstDayOfTerm] = useState<string>(termData.firstDayOfTerm || ``);
  const [coursesList, setCoursesList] = useState<CoursesList>([]);
  const [selectedTimetable, setSelectedTimetable] = useState<number>(0);
  const [displayTimetables, setDisplayTimetables] = useState<DisplayTimetablesMap>({});
  const [courseData, setCourseData] = useState<CourseDataMap>({ map: [] });

  const themeObject = useMemo(() => {
    return isDarkMode ? darkTheme(currentTheme) : lightTheme(currentTheme);
  }, [currentTheme, isDarkMode]);

  const initialContext: IAppContext = {
    themeObject,
    currentTheme,
    setCurrentTheme,
    is12HourMode,
    setIs12HourMode,
    isDarkMode,
    setIsDarkMode,
    isSquareEdges,
    setIsSquareEdges,
    isShowOnlyOpenClasses,
    setisShowOnlyOpenClasses,
    isDefaultUnscheduled,
    setIsDefaultUnscheduled,
    isHideClassInfo,
    setIsHideClassInfo,
    isHideExamClasses,
    setIsHideExamClasses,
    isConvertToLocalTimezone,
    setIsConvertToLocalTimezone,
    alertMsg,
    setAlertMsg,
    alertFunction,
    setAlertFunction,
    errorVisibility,
    setErrorVisibility,
    infoVisibility,
    setInfoVisibility,
    autoVisibility,
    setAutoVisibility,
    isDrag,
    setIsDrag,
    days,
    setDays,
    earliestStartTime,
    setEarliestStartTime,
    latestEndTime,
    setLatestEndTime,
    term,
    setTerm,
    termName,
    setTermName,
    termsData,
    setTermsData,
    termNumber,
    setTermNumber,
    year,
    setYear,
    firstDayOfTerm,
    setFirstDayOfTerm,
    coursesList,
    setCoursesList,
    selectedTimetable,
    setSelectedTimetable,
    displayTimetables,
    setDisplayTimetables,
    courseData,
    setCourseData,
  };

  return <AppContext.Provider value={initialContext}>{children}</AppContext.Provider>;
};

export default AppContextProvider;
